import {
    forwardRef,
    useImperativeHandle,
    useState,
    useContext,
    useRef,
} from 'react'

import ApplicantDataFormContext from '../../../../../Contexts/ApplicantDataFormContext'

import TextInput from '../../../../../Components/TextInput'

// Name validator
const nameValidator = (val) => /^[\p{L} '-]{2,50}$/u.test(val.trim());

const NoticeCityNewYorkCity = forwardRef(({ data, company, willVerificationBeApplied, refToPrint }, ref) => {

    const applicantDataForm = useContext(ApplicantDataFormContext)

    const fullName = applicantDataForm.current["fullName"]
    const ssnLastFourDigit = applicantDataForm.current["ssnLastFourDigit"]

    const [requestReceiptCopy, setRequestReceiptCopy] = useState(data?.requestReceiptCopy ?? false)
    const [agreeChecked, setAgreeChecked] = useState(data?.agreeChecked ?? false)
    const [lastFourSSN, setLastFourSSN] = useState(ssnLastFourDigit || (data?.lastFourSSN ?? ''))
    const [error, setError] = useState("")

    const todayDate = new Date().toLocaleDateString()

    const fullNameRef = useRef()

    useImperativeHandle(ref, () => ({
        validateAndGetData: () => {

            let isValid = agreeChecked
            const fullNameData = fullNameRef.current.validateAndGetData()

            if (!isValid) {
                setError("You need to agree on electronic signature")
            }

            if (isValid) {
                isValid = lastFourSSN.length === 4

                if (!isValid) {
                    setError("You need to fill last 4 digit of your SSN")
                }

                if (isValid && ssnLastFourDigit !== lastFourSSN) {
                    isValid = false
                    setError("Last 4 digits of SSN does not match the one you provided on previous pages.")
                }

            }

            isValid = isValid && fullNameData.isValid

            if (isValid && fullName !== fullNameData.value) {
                isValid = false
                setError("Full Legal Name does not match the one you provided on previous pages.")
            }

            if (isValid) {
                setError("")
            }

            return {
                isValid,
                value: {
                    agreeChecked,
                    requestReceiptCopy,
                    lastFourSSN,
                    fullName,
                },
            }
        },
    }))

    return (
        <div className="px-4 py-10 max-w-3xl mx-auto">
            <div ref={refToPrint} className="flex flex-col space-y-6">
                <h1 className="text-2xl font-bold text-gray-800 text-center">
                    New York City Notice
                </h1>

                <div className="space-y-4 text-gray-700 text-sm sm:text-base">
                    <p>
                        By clicking the box below, you further authorize , the "Company," to provide you with a copy of your consumer report,
                        the New York City Fair Chance Act Notice form, and any other documents, to the extent required by law, at the mailing
                        address and/or email address you provide to the Company
                    </p>

                    <h2 className="font-semibold text-gray-800 mt-6">Your Electronic Signature</h2>

                    <p>
                        I understand that by checking the "I AGREE" box below, typing my name and the last four digits of my Social Security Number
                        or National ID Number, and clicking on the "SIGN ACKNOWLEDGMENT" button, this constitutes my electronic signature, dated as of when
                        I click the button. By doing so, I:
                    </p>

                    <ul className="list-disc list-inside space-y-1">
                        <li>
                            Am authorizing {company?.name} to conduct the background check(s) described above.
                        </li>
                        <li>
                            Am consenting to use electronic means to sign this form and confirm that I have read and understand all applicable
                            disclosures and this Authorization.
                        </li>
                        <li>
                            Acknowledge I may request a hard copy of this Disclosure and Authorization form after agreeing to the background check
                            electronically by contacting VerifiedID at <a href="tel:+18887182597" className="text-blue-600 underline">(888) 718-2597</a>.
                        </li>
                    </ul>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 items-end">

                        <div>

                            <TextInput
                                ref={fullNameRef}
                                title="Full Legal Name"
                                data={fullName}
                                description="Provide full legal name, including middle name"
                                isMandatory={true}
                                validator={nameValidator}
                            />

                        </div>

                        <div className='my-2'>
                            <label className="text-sm text-gray-600 font-medium">Last 4 digits of SSN/National ID Number*</label>
                            <input
                                value={lastFourSSN}
                                onChange={(e) => setLastFourSSN(e.target.value)}
                                maxLength={4}
                                className="mt-1 w-full border border-gray-300 rounded-md px-4 py-2 text-gray-700"
                                placeholder="1234"
                            />
                        </div>

                        <div>
                            <label className="text-sm text-gray-600 font-medium">Date</label>
                            <input
                                value={todayDate}
                                readOnly
                                className="mt-1 w-full border border-gray-300 rounded-md px-4 py-2 bg-gray-100 text-gray-700"
                            />
                        </div>
                    </div>

                    <label className="flex items-start space-x-2">
                        <input
                            type="checkbox"
                            className="mt-1"
                            onChange={() => setAgreeChecked(!agreeChecked)}
                            checked={agreeChecked}
                        />
                        <span>I AGREE</span>
                    </label>

                </div>
            </div>

            {error && (
                <p className="mt-1 text-sm text-red-600">{error}</p>
            )}
        </div>
    )

})

export default NoticeCityNewYorkCity
